// Dutch labels for the external-context benchmark dimensions (CBS neighbourhood
// context). A dimension key is `${grain}${Metric}Quartile` — e.g. hhIncomeQuartile,
// personaKoopwoningQuartile. The grain says whose neighbourhoods (households /
// deelnemers / activiteiten); the metric + quartile say what about them.
//
// Quartiles are against the national buurt distribution: Q1 = bottom 25% of NL
// buurten on that metric, Q4 = top 25%.
//
// AI-CONTEXT: See docs/features/external-context-layer.md

const GRAINS = ['hh', 'persona', 'activity']
const METRICS = ['Income', 'Woz', 'Koopwoning', 'Dichtheid', 'Aardgas']

// All context dimension keys, in a stable order (grain-major).
const CONTEXT_DIMENSION_KEYS = GRAINS.flatMap(grain => METRICS.map(metric => `${grain}${metric}Quartile`))

// Subject phrase per grain — top-performer framing ("de huishoudens …").
const CONTEXT_SUBJECT = {
    hh: 'de huishoudens',
    persona: 'de deelnemers',
    activity: 'de activiteiten',
}

// Short titles for table display.
const GRAIN_TITLE = {
    hh: 'Huishoudens',
    persona: 'Deelnemers',
    activity: 'Activiteiten',
}

const METRIC_LABEL = {
    Income: 'Gemiddeld inkomen',
    Woz: 'WOZ-waarde',
    Koopwoning: 'Koopwoningen',
    Dichtheid: 'Bevolkingsdichtheid',
    Aardgas: 'Aardgasverbruik',
}

// Quartile → Dutch phrase per metric.
const METRIC_BUCKET_LABELS = {
    Income: {
        Q1: 'een laag gemiddeld inkomen',
        Q2: 'een ondergemiddeld inkomen',
        Q3: 'een bovengemiddeld inkomen',
        Q4: 'een hoog gemiddeld inkomen',
    },
    Woz: {
        Q1: 'een lage WOZ-waarde',
        Q2: 'een ondergemiddelde WOZ-waarde',
        Q3: 'een bovengemiddelde WOZ-waarde',
        Q4: 'een hoge WOZ-waarde',
    },
    Koopwoning: {
        Q1: 'weinig koopwoningen (veel huur)',
        Q2: 'relatief weinig koopwoningen',
        Q3: 'relatief veel koopwoningen',
        Q4: 'veel koopwoningen',
    },
    Dichtheid: {
        Q1: 'een lage bevolkingsdichtheid (landelijk)',
        Q2: 'een ondergemiddelde bevolkingsdichtheid',
        Q3: 'een bovengemiddelde bevolkingsdichtheid',
        Q4: 'een hoge bevolkingsdichtheid (stedelijk)',
    },
    Aardgas: {
        Q1: 'een laag gemiddeld aardgasverbruik',
        Q2: 'een ondergemiddeld aardgasverbruik',
        Q3: 'een bovengemiddeld aardgasverbruik',
        Q4: 'een hoog gemiddeld aardgasverbruik',
    },
}

// Parse a dimension key → { grain, metric } or null if it isn't a context dim.
const parseContextDim = (key) => {
    const m = typeof key === 'string' && key.match(/^(hh|persona|activity)(Income|Woz|Koopwoning|Dichtheid|Aardgas)Quartile$/)
    return m ? { grain: m[1], metric: m[2] } : null
}

const isContextDim = (key) => parseContextDim(key) !== null

const contextSubject = (key) => {
    const p = parseContextDim(key)
    return p ? CONTEXT_SUBJECT[p.grain] : ''
}

// Quartile (Q1–Q4) → Dutch phrase for this dimension's metric.
const contextBucketLabel = (key, quartile) => {
    const p = parseContextDim(key)
    if (!p) return quartile
    return (METRIC_BUCKET_LABELS[p.metric] || {})[quartile] || quartile
}

// Table title for a dimension, e.g. "Deelnemers · Gemiddeld inkomen".
const contextDimensionTitle = (key) => {
    const p = parseContextDim(key)
    if (!p) return key
    return `${GRAIN_TITLE[p.grain]} · ${METRIC_LABEL[p.metric]}`
}

export {
    CONTEXT_DIMENSION_KEYS,
    CONTEXT_SUBJECT,
    GRAIN_TITLE,
    METRIC_LABEL,
    METRIC_BUCKET_LABELS,
    parseContextDim,
    isContextDim,
    contextSubject,
    contextBucketLabel,
    contextDimensionTitle,
}
